import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF8] flex">
      {/* Left side - decorative image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/f80a26d9-6372-4496-9e90-9bd77818ba19/images/f300ca52ae99289ad2b7cd90d476281037e2939c5419b407d9eda9a088d14c7b.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F8FAF8]/30" />
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="bg-white border-[#E2E8F0] shadow-lg">
            <CardHeader className="text-center space-y-4">
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vitality-connect-3/artifacts/8w9yb7do_zdravestravovani_logo.png" 
                  alt="Zdravé stravování" 
                  className="h-16 object-contain"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-[#052E16] font-['Manrope']">Přihlášení</CardTitle>
              <CardDescription className="text-[#57534E]">
                Přihlaste se do intranetu
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="p-3 bg-[#FEE2E2] border border-[#FECACA] rounded-xl text-[#991B1B] text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1C1917]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vas@email.cz"
                    required
                    data-testid="login-email-input"
                    className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1C1917]">Heslo</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                    className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                  />
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4">
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                  className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl transition-all shadow-sm"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Přihlásit se
                </Button>
                
                <p className="text-sm text-[#57534E] text-center">
                  Nemáte účet?{' '}
                  <Link to="/register" className="text-[#16A34A] hover:text-[#15803D] font-medium transition-colors">
                    Zaregistrujte se
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
